import { getExtensionDependencyFromEditor } from "@lexical/extension";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useEffect, useSyncExternalStore, type JSX } from "react";
import { ClipboardExtension } from "../features/clipboard/extension";
import type { FragmentNote } from "../index";
import type { EditorLabels } from "./labels";

const VISIBLE_MS = 8_000;

const NOTE_LABELS: Readonly<Record<FragmentNote, keyof EditorLabels>> = {
  "merged-cells-split": "pasteMergedCellsSplit",
  "nested-table-flattened": "pasteNestedTableFlattened",
  "cell-blocks-flattened": "pasteCellBlocksFlattened",
  "table-flattened": "pasteTableFlattened",
  "attachment-flattened": "pasteAttachmentFlattened",
  "images-dropped": "pasteImagesDropped",
};

/**
 * Says that a paste was refused, because a paste that silently does nothing looks like a broken
 * editor, and says when one went in changed (merged cells split, a table turned into text),
 * because content that silently changes shape looks like a broken paste. The region is always in
 * the page and only its text changes, which is what makes a polite live region announce reliably.
 */
export function PasteNotice({ labels }: { labels: EditorLabels }): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const clipboard = getExtensionDependencyFromEditor(editor, ClipboardExtension).output;
  const rejection = useSyncExternalStore(
    clipboard.subscribe,
    clipboard.getRejection,
    clipboard.getRejection,
  );
  const adjustment = useSyncExternalStore(
    clipboard.subscribe,
    clipboard.getAdjustment,
    clipboard.getAdjustment,
  );
  // Whichever there is. A new one is a new object, which is what restarts the timer.
  const notice = rejection ?? adjustment;
  const open = notice !== null;

  useEffect(() => {
    if (notice === null) return;
    const timer = setTimeout(clipboard.dismiss, VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [notice, clipboard]);

  const message =
    rejection !== null
      ? labels.pasteTooLarge
      : (adjustment?.notes ?? []).map((note) => labels[NOTE_LABELS[note]]).join(" ");

  return (
    <output
      className="ze-notice"
      data-open={open}
      data-kind={rejection !== null ? "refused" : "adjusted"}
    >
      {open && (
        <>
          <span>{message}</span>
          <button type="button" className="ze-notice-dismiss" onClick={clipboard.dismiss}>
            {labels.dismiss}
          </button>
        </>
      )}
    </output>
  );
}
